import React, { useCallback, useEffect } from 'react';

import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { HomeRecentPosts } from './home-recent-posts';
import { OurServices } from './our-services';
import { Plans } from './plans';
import { StyledLandingPage } from './styles';
import { useNotificationContext } from '../../components/molecules/notification-context';
import { AIShowcase } from '../../components/organisms/ai-showcase';
import { LandingHeader } from '../../components/organisms/landing-header';
import { LandingMenu } from '../../components/organisms/landing-menu';
import { MainFooter } from '../../components/organisms/main-footer';
import { SocialProof } from '../../components/organisms/social-proof';
import { TopMenu } from '../../components/organisms/top-menu';
import { useAuth } from '../../hooks/use-auth';
import LandingHeroMobileBg from '../../theme/assets/backgrounds/bg-landing-hero-mobile.webp';
import LandingHeroBg from '../../theme/assets/backgrounds/bg-landing-hero.webp';
import { AnalyticsEvents, trackEvent } from '../../utils/analytics';
import { NOTIFICATION_TYPES } from '../../utils/constants';
import { canonical, ogImage, ogUrl } from '../../utils/seo';
import {
  faqPageSchema,
  LANDING_PAGE_FAQS,
  organizationSchema,
  softwareApplicationSchema,
} from '../../utils/seo-schemas';

const LandingPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { userSignIn } = useAuth();
  const notificationApi = useNotificationContext();

  const goToOnboarding = useCallback(() => {
    navigate('/onboarding');
  }, [navigate]);

  useEffect(() => {
    const success = params.get('success');

    if (!success) return;

    if (success === 'true') {
      const token = params.get('token');
      const complete = params.get('complete');

      if (token) {
        trackEvent(AnalyticsEvents.LOGIN, { method: 'twitter' });
        userSignIn({
          token,
          redirectUrl: complete === 'true' ? '/user/dashboard' : '/onboarding',
        });
      }
    } else {
      const error = params.get('error');
      const message = error === 'twitter_failed' ? 'An error occurred while trying to login with X' : 'Unknown error';

      notificationApi[NOTIFICATION_TYPES.error]({ message, pauseOnHover: true });
      window.history.replaceState({}, '', '/');
    }
  }, [notificationApi, params, userSignIn]);

  return (
    <StyledLandingPage>
      <Helmet>
        <title>Active Shape - AI-Powered Nutrition &amp; Fitness App</title>
        <meta
          name="description"
          content="AI-powered nutrition plans, calorie and macro tracking, food scanner, fasting tracker, and daily AI health insights, all personalized to your goals. Start your transformation today."
        />
        {canonical('/') && <link rel="canonical" href={canonical('/')} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Active Shape - AI-Powered Nutrition & Fitness App" />
        <meta
          property="og:description"
          content="AI-powered nutrition plans, calorie and macro tracking, food scanner, fasting tracker, and daily AI health insights, all personalized to your goals. Start your transformation today."
        />
        {ogImage() && <meta property="og:image" content={ogImage()} />}
        {ogUrl('/') && <meta property="og:url" content={ogUrl('/')} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Active Shape - AI-Powered Nutrition & Fitness App" />
        <meta
          name="twitter:description"
          content="AI-powered nutrition plans, calorie and macro tracking, food scanner, fasting tracker, and daily AI health insights, all personalized to your goals. Start your transformation today."
        />
        {ogImage() && <meta name="twitter:image" content={ogImage()} />}
        <link rel="preload" as="image" href={LandingHeroBg} media="(min-width: 1051px)" fetchPriority="high" />
        <link rel="preload" as="image" href={LandingHeroMobileBg} media="(max-width: 1050px)" fetchPriority="high" />
        <script type="application/ld+json">{JSON.stringify(softwareApplicationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(faqPageSchema(LANDING_PAGE_FAQS))}</script>
      </Helmet>
      <div className="landing-page">
        <div className="landing-page__mobile-menu">
          <TopMenu />
        </div>
        <LandingHeader
          backgroundImage={LandingHeroBg}
          mobileBackgroundImage={LandingHeroMobileBg}
          onCtaClick={goToOnboarding}
        />
        <div className="landing-page__desktop-menu">
          <LandingMenu />
        </div>
        <div className="landing-page__container">
          <OurServices />
          <AIShowcase />
          <SocialProof />
          <Plans />
          <HomeRecentPosts />
        </div>
        <MainFooter />
      </div>
    </StyledLandingPage>
  );
};

export default LandingPage;
